import {
  Wand2,
  Sun,
  Sofa,
  Video,
  FileImage,
  Globe,
  FileText,
  Languages,
  Share2,
  QrCode,
  Smartphone,
  PenTool,
  Crown,
  Clapperboard,
  Cloud,
  Leaf,
  type LucideIcon,
} from 'lucide-react';

import qualityBefore from '@/assets/quality-before.jpg';
import qualityAfter from '@/assets/quality-after.jpg';
import stagingBefore from '@/assets/staging-before.jpg';
import stagingAfter from '@/assets/staging-after.jpg';
import goldenHourBefore from '@/assets/golden-hour-before.jpg';
import goldenHourAfter from '@/assets/golden-hour-after.jpg';
import customPromptBefore from '@/assets/custom-prompt-before.jpg';
import customPromptAfter from '@/assets/custom-prompt-after.jpg';
import heroAfter from '@/assets/hero-after.jpg';
import clutterAfter from '@/assets/clutter-after.jpg';
import skyLawnAfter from '@/assets/sky-lawn-after.jpg';
import blueSkyBefore from '@/assets/blue-sky-before.jpg';
import blueSkyAfter from '@/assets/blue-sky-after.jpg';
import lawnReplacementBefore from '@/assets/lawn-replacement-before.jpg';
import lawnReplacementAfter from '@/assets/lawn-replacement-after.jpg';

export type FeatureItem = {
  id: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  description: string;
  benefits: string[];
  beforeImage?: string;
  afterImage?: string;
  hasComparison?: boolean;
  extraIcons?: LucideIcon[];
  /** Optional sample video URL shown in "See it in action" instead of an image comparison */
  sampleVideo?: string;
  /** Source photos that were used to generate the sample video (shown beneath the player). */
  sampleVideoSourcePhotos?: { src: string; label: string }[];
  /** Why this feature is good and helpful */
  whyItHelps: string[];
  /** How it improves the listing and helps sell faster / for more */
  sellingImpact: string[];
  /** Short pitch for the dropdown */
  shortPitch: string;
  /** SEO meta title (≤60 chars). Falls back to `${title} – ListBright Features`. */
  seoTitle?: string;
  /** SEO meta description (≤160 chars). Falls back to tagline. */
  seoDescription?: string;
  /** SEO keywords. Joined with commas in the meta keywords tag. */
  keywords?: string[];
  /** Long-form, keyword-rich paragraphs rendered in the "Deep dive" section. */
  longDescription?: string[];
  /** Concrete use cases to drive intent and rank for long-tail queries. */
  useCases?: { title: string; body: string }[];
  /** Headline stats / social proof shown as a strip. */
  stats?: { value: string; label: string }[];
  /** FAQs rendered as accordion + emitted as FAQPage JSON-LD. */
  faqs?: { q: string; a: string }[];
  /** Optional override for the bottom CTA copy. */
  ctaHeadline?: string;
  ctaSubtext?: string;
};

export const features: FeatureItem[] = [
  {
    id: 'virtual-staging',
    icon: Sofa,
    title: 'Virtual Staging',
    tagline: 'Furnish empty rooms with beautiful, realistic furniture',
    shortPitch: 'Furnish empty rooms so buyers can picture living there.',
    description:
      'Empty rooms can feel cold and uninviting. Our AI adds stylish, realistic furniture and decor that helps buyers visualize the space as a home. Choose from modern, traditional, or minimalist styles.',
    benefits: [
      'Realistic furniture placement with proper shadows',
      'Multiple style options to match the property',
      'Removes existing clutter before staging',
      'Perfect for vacant properties or rentals',
    ],
    beforeImage: stagingBefore,
    afterImage: stagingAfter,
    whyItHelps: [
      'Most buyers struggle to visualize empty spaces — staging removes that mental work.',
      'Costs a fraction of physical staging (which can run thousands per property).',
      'Lets you re-stage the same room in different styles to appeal to different buyer profiles.',
    ],
    sellingImpact: [
      'Industry research consistently shows staged homes sell faster than vacant ones.',
      'Staged listings often command higher offers because the space feels move-in ready.',
      'Reduces price-cut risk by avoiding the “cold and empty” perception that lingers on portals.',
    ],
    seoTitle: 'AI Virtual Staging for Real Estate | ListBright',
    seoDescription: 'Furnish empty rooms with realistic AI virtual staging. Multiple styles, accurate shadows, and a fraction of the cost of physical staging.',
    keywords: ['virtual staging', 'AI virtual staging real estate', 'furnish empty room AI', 'digital home staging', 'staging software for realtors'],
    longDescription: [
      'Empty rooms are one of the biggest reasons buyers scroll past a listing — they feel cold, look smaller, and force buyers to imagine furniture themselves. ListBright\'s AI Virtual Staging fixes this in seconds.',
      'Upload a photo of any empty space and choose a style — modern, Scandinavian, mid-century, classic, or minimalist. Our model adds realistic furniture with correct scale, perspective and shadows, so the staged image looks like a real room, not a render.',
      'It works for living rooms, bedrooms, dining areas, home offices and outdoor terraces. Compared with traditional virtual-staging services that cost $30–$80 per photo and take 24–48 hours, ListBright stages an entire listing in minutes for a fraction of the price.',
    ],
    useCases: [
      { title: 'Vacant resale homes', body: 'Stage every empty room before going to market so the listing photos feel warm and lived-in.' },
      { title: 'New-build developments', body: 'Show buyers what a finished unit could look like without building a physical show flat.' },
      { title: 'Rentals between tenants', body: 'Help short-stay and long-let landlords market quickly while the unit is still empty.' },
      { title: 'Re-style for the buyer', body: 'Run the same room through several styles to appeal to different buyer demographics.' },
    ],
    stats: [
      { value: '$1–$3', label: 'Per staged photo (vs $30–$80 traditional)' },
      { value: 'Minutes', label: 'Not 24–48 hours' },
      { value: '5+', label: 'Furniture styles to choose from' },
    ],
    faqs: [
      { q: 'Is AI virtual staging legal to use on listings?', a: 'Yes, on every major portal — provided staged images are clearly disclosed as virtually staged. Most regulators (NAR in the US, RICS in the UK, etc.) explicitly allow it with disclosure.' },
      { q: 'Will the furniture look fake?', a: 'No. Our model places furniture with correct perspective, scale and lighting so it blends naturally into the photo. Buyers see a believable room, not an obvious render.' },
      { q: 'Can I stage a room that already has furniture?', a: 'Yes — ListBright can remove existing clutter or furniture first, then re-stage the room in your chosen style.' },
      { q: 'How is this different from physical staging?', a: 'Physical staging can cost thousands and takes days to install. AI virtual staging happens in minutes, costs a few dollars, and works on any number of rooms.' },
    ],
    ctaHeadline: 'Stage your empty rooms in minutes',
    ctaSubtext: 'Pick a style, upload a photo, and watch a believable, beautifully furnished room appear — for the cost of a coffee.',
  },
  {
    id: 'photo-enhancement',
    icon: Wand2,
    title: 'Photo Quality Enhancement',
    tagline: 'Transform amateur photos into professional-grade images',
    shortPitch: 'Pro-grade lighting, color, and sharpness on every photo.',
    description:
      'Our AI automatically corrects lighting, enhances colors, sharpens details, and removes noise from your property photos. The result? Images that look like they were taken by a professional photographer.',
    benefits: [
      'Automatic color correction and white balance',
      'HDR-like dynamic range enhancement',
      'Noise reduction and sharpening',
      'Perspective and lens distortion correction',
    ],
    beforeImage: qualityBefore,
    afterImage: qualityAfter,
    whyItHelps: [
      'Buyers scroll past dim, flat photos in seconds — bright, crisp images make them stop and look.',
      'Replaces the cost and scheduling of a professional photographer with a one-click result.',
      'Levels the playing field: your phone photos look like a €300 photo shoot.',
    ],
    sellingImpact: [
      'Listings with high-quality photos get noticeably more clicks and saves on portals.',
      'Better first-impression photos drive more in-person viewings — the strongest predictor of an offer.',
      'Professional-looking imagery supports a higher asking price by signaling a well-cared-for property.',
    ],
    seoTitle: 'AI Real Estate Photo Enhancement | ListBright',
    seoDescription: 'Turn dim phone photos into bright, professional real estate listing images in seconds. AI color, lighting, HDR & sharpening — no photographer needed.',
    keywords: ['real estate photo enhancement', 'AI photo editor for realtors', 'property photo enhancer', 'HDR real estate photos', 'enhance listing photos'],
    longDescription: [
      'ListBright\'s AI Photo Enhancement is built specifically for real estate. It analyses every property photo, then automatically corrects exposure, white balance, dynamic range, sharpness and noise — the same edits a $300 professional shoot would deliver, applied in seconds.',
      'Unlike generic photo filters, our model understands real-estate context: it brightens dark interiors without blowing out windows, recovers blue sky from overcast exteriors, neutralises yellow tungsten light, and straightens lens distortion so rooms look bigger and more honest.',
      'The result is a complete set of bright, sharp, true-to-life listing photos — ready for Zillow, Rightmove, Idealista, Bayut, Booking.com and every social channel — without scheduling a photographer or learning Lightroom.',
    ],
    useCases: [
      { title: 'Phone-shot interiors', body: 'Turn iPhone or Android photos into magazine-grade interior shots with balanced light and natural colour.' },
      { title: 'Old MLS photos', body: 'Refresh tired listings already on the market with one click — no re-shoot, no agent visit.' },
      { title: 'Rentals & short-stays', body: 'Owners on Airbnb, Booking and Vrbo see higher click-through with crisp, well-lit cover photos.' },
      { title: 'New developments', body: 'Quickly polish dozens of unit photos to a consistent, brand-ready standard.' },
    ],
    stats: [
      { value: '~10 min', label: 'From upload to ready-to-post' },
      { value: '$0', label: 'Photographer cost replaced' },
      { value: '20+', label: 'Photos enhanced per listing' },
    ],
    faqs: [
      { q: 'Will the AI make my photos look fake or over-edited?', a: 'No. Our model is tuned for real estate, where buyers expect honest, natural imagery. It corrects light and colour rather than adding artificial effects, so the home still looks like the home buyers will visit.' },
      { q: 'Do I need a professional camera?', a: 'No. ListBright is designed for phone photos. Modern smartphones capture more than enough detail — our AI handles the lighting and colour issues that usually let phone shots down.' },
      { q: 'Can I use the enhanced photos on Zillow, Rightmove or Idealista?', a: 'Yes. Enhanced images export at high resolution and are accepted by every major real-estate portal worldwide.' },
      { q: 'How long does it take?', a: 'A full set of 10–20 photos is typically enhanced in under 10 minutes, fully automatically.' },
    ],
    ctaHeadline: 'Enhance your first listing photos free',
    ctaSubtext: 'Upload your phone photos and get a full set of professional-grade, portal-ready images in minutes.',
  },

  {
    id: 'golden-hour',
    icon: Sun,
    title: 'Golden Hour',
    tagline: 'Add warm, inviting sunset lighting to any photo',
    shortPitch: 'Turn any exterior into a stunning sunset shot.',
    description:
      "Golden hour photos get more engagement, but you can't always shoot at sunset. Our AI transforms any daytime exterior photo into a stunning golden hour shot with warm, inviting light.",
    benefits: [
      'Warm, inviting atmosphere',
      'Enhanced curb appeal',
      'Works on any time of day',
      'Natural-looking results',
    ],
    beforeImage: goldenHourBefore,
    afterImage: goldenHourAfter,
    whyItHelps: [
      'Saves you from waiting days for the right weather and light.',
      'Warm tones trigger an emotional “this feels like home” response in buyers.',
      'Makes ordinary exteriors look aspirational — perfect for the lead photo on a portal.',
    ],
    sellingImpact: [
      'A more eye-catching cover photo is the single biggest lever for click-through on listing portals.',
      'More clicks → more enquiries → more viewings → more offers, often at better prices.',
      'Helps justify a premium price by making the property feel like a lifestyle, not just four walls.',
    ],
    seoTitle: 'AI Golden Hour Photo Effect for Real Estate | ListBright',
    seoDescription: 'Turn any daytime exterior into a stunning golden-hour shot. Warm sunset light, blue twilight skies and instant curb appeal — no waiting for the right weather.',
    keywords: ['golden hour real estate photos', 'twilight photo AI', 'sunset photo effect', 'real estate sky replacement', 'curb appeal photos'],
    longDescription: [
      'Golden-hour and twilight shots are the most-clicked exterior photos on every major real-estate portal — but capturing them requires perfect weather, perfect timing, and often a return trip to the property. ListBright generates the same look from a single daytime photo.',
      'Our AI rebuilds the lighting on the property: warm directional sun on facades, soft shadows on lawns, glowing windows, and a sky that matches the time of day you choose. The result feels photographed, not filtered.',
      'Use it on the lead photo of your listing, on your Instagram and Facebook ads, or on the cover of a property flyer — anywhere a striking first impression converts scrolls into clicks.',
    ],
    useCases: [
      { title: 'Lead listing photo', body: 'Replace a flat midday exterior with a warm golden-hour cover shot that stops the scroll on portals.' },
      { title: 'Instagram & Facebook ads', body: 'Twilight exteriors out-perform standard photos in paid social campaigns.' },
      { title: 'Luxury & lifestyle homes', body: 'Position high-end properties as aspirational rather than transactional.' },
      { title: 'Cloudy-day reshoots', body: 'Salvage exterior photos taken in grey, flat or rainy conditions.' },
    ],
    stats: [
      { value: 'Any time of day', label: 'No need to wait for sunset' },
      { value: '1 photo', label: 'Instead of a return shoot' },
      { value: 'Photo-real', label: 'Lighting, shadows and reflections' },
    ],
    faqs: [
      { q: 'Does this just add a warm filter?', a: 'No. The AI relights the entire scene — sky, facades, shadows and reflections — so the photo looks genuinely shot at golden hour, not filtered.' },
      { q: 'Is it allowed on real-estate portals?', a: 'Yes, as long as you disclose enhanced photos where required by your local regulator. The home itself is unchanged.' },
      { q: 'Can I choose between sunset and twilight?', a: 'Yes. ListBright supports several time-of-day looks, from warm golden hour to deep blue twilight with lit windows.' },
      { q: 'Will it work on apartments and city buildings?', a: 'Yes — it works for houses, apartments, terraces, commercial buildings and land plots.' },
    ],
    ctaHeadline: 'Make every exterior shot worthy of the cover',
    ctaSubtext: 'Generate a golden-hour or twilight version of your hero photo — in seconds, from any daytime shot.',
  },

  {
    id: 'blue-sky',
    icon: Cloud,
    title: 'Blue Sky Replacement',
    tagline: 'Swap dull, grey skies for vibrant blue ones',
    shortPitch: 'Replace grey, overcast skies with a clear blue sky.',
    description:
      'Overcast and washed-out skies make exteriors feel flat and uninviting. Our AI replaces a dull sky with a vibrant blue one — soft cumulus clouds, accurate lighting on the building, natural reflections — so every exterior shot looks like it was photographed on a perfect day.',
    benefits: [
      'Natural blue sky with soft, realistic clouds',
      'Lighting on the property re-balanced to match',
      'Works on houses, apartments, land and commercial',
      'One-click — no Photoshop or masking required',
    ],
    beforeImage: blueSkyBefore,
    afterImage: blueSkyAfter,
    whyItHelps: [
      'No more waiting weeks for a sunny day to re-shoot.',
      'Grey skies make even great homes look depressing — blue skies sell the lifestyle.',
      'Consistent skies across a full set make your listings look professionally produced.',
    ],
    sellingImpact: [
      'Bright, sunny exteriors lift click-through on the lead photo — the single biggest conversion lever on portals.',
      'A consistent, premium look reinforces a higher asking price.',
      'Helps cloudy-region listings compete with sunny-climate competitors on the same portals.',
    ],
    seoTitle: 'AI Blue Sky Replacement for Real Estate Photos | ListBright',
    seoDescription: 'Swap grey, overcast skies for a vibrant blue sky in one click. Natural clouds, balanced lighting, ready for portals and social — no Photoshop needed.',
    keywords: ['blue sky replacement', 'AI sky replacement', 'real estate sky replacement', 'overcast sky fix', 'fix cloudy real estate photos'],
    longDescription: [
      'On every major real-estate portal, exterior photos with bright blue skies out-perform photos with grey, overcast skies on click-through, saves, and enquiries. ListBright\'s Blue Sky Replacement gives every listing that look — without a re-shoot.',
      'Our model rebuilds the sky with natural cumulus clouds, then re-balances the lighting on the building, the lawn and reflective surfaces so the new sky feels genuinely photographed, not pasted on.',
      'Use it on lead listing photos, hero shots for microsites, social ads and flyers — anywhere a bright, optimistic first impression converts more scrolls into clicks.',
    ],
    useCases: [
      { title: 'Cloudy-day re-shoots', body: 'Salvage exteriors taken on overcast or rainy days without scheduling a return visit.' },
      { title: 'Lead listing photo', body: 'Replace a flat grey sky with a vivid blue one so the cover photo stops the scroll on portals.' },
      { title: 'Northern-climate markets', body: 'Make listings in cloud-prone regions feel as bright and appealing as sunny-market competitors.' },
      { title: 'Consistent photo sets', body: 'Match skies across an entire set so your listings look professionally produced.' },
    ],
    stats: [
      { value: 'Any weather', label: 'No need to wait for sun' },
      { value: '1 click', label: 'No masking or Photoshop' },
      { value: 'Photo-real', label: 'Lighting and reflections re-balanced' },
    ],
    faqs: [
      { q: 'Will the new sky look fake?', a: 'No. The AI matches cloud type, sun direction and overall lighting so the sky blends naturally into the photo, including reflections on windows and water.' },
      { q: 'Is this allowed on real-estate portals?', a: 'Yes, on every major portal — provided enhanced photos are disclosed where your local regulator requires it. The property itself is unchanged.' },
      { q: 'Can I pick the sky style?', a: 'Yes. Choose a clear blue sky, light scattered clouds or a more dramatic look depending on the property.' },
    ],
    ctaHeadline: 'Give every exterior a perfect blue sky',
    ctaSubtext: 'Replace dull, grey skies with a vibrant blue one in seconds — no re-shoot, no Photoshop.',
  },

  {
    id: 'lawn-replacement',
    icon: Leaf,
    title: 'Lawn Replacement',
    tagline: 'Turn patchy, brown lawns into lush green ones',
    shortPitch: 'Replace patchy, brown grass with a lush green lawn.',
    description:
      'Brown, patchy lawns make homes look neglected. Our AI replaces tired grass with a healthy, perfectly manicured green lawn — natural texture, realistic edges, accurate lighting — so the front of the house finally matches the rest of the listing.',
    benefits: [
      'Lush, healthy green grass with natural texture',
      'Realistic edges along paths, beds and driveways',
      'Lighting and shadows blended into the scene',
      'Works on front lawns, back gardens and side yards',
    ],
    beforeImage: lawnReplacementBefore,
    afterImage: lawnReplacementAfter,
    whyItHelps: [
      'Skip the cost of resodding or weeks of lawn care before listing.',
      'A lush lawn signals a well-cared-for property — buyers project that onto the rest of the home.',
      'Off-season listings (autumn, winter, drought) can finally show the property at its best.',
    ],
    sellingImpact: [
      'Curb appeal is the single biggest driver of first-impression value — a great lawn lifts perceived price.',
      'Lead photos with a healthy lawn get noticeably more clicks on portals than dry, patchy ones.',
      'Stronger curb appeal supports both higher asking prices and quicker offers.',
    ],
    seoTitle: 'AI Lawn Replacement for Real Estate Photos | ListBright',
    seoDescription: 'Replace patchy, brown grass with a lush green lawn in one click. Natural texture, realistic edges and balanced lighting — perfect curb appeal in seconds.',
    keywords: ['lawn replacement AI', 'green grass photo edit', 'curb appeal AI', 'real estate lawn enhancer', 'fix brown lawn photo'],
    longDescription: [
      'A patchy, brown lawn is one of the fastest ways to make a great home look tired. ListBright\'s Lawn Replacement rebuilds the grass with a lush, healthy green texture — keeping the layout, edges and surrounding landscaping exactly as they are.',
      'The model matches the photo\'s lighting, shadow direction and perspective, so the new lawn feels genuinely photographed in that scene. Paths, flower beds and driveways stay crisp; only the grass changes.',
      'Use it on cover photos, garden shots and aerial views — anywhere curb appeal drives first impressions and enquiries.',
    ],
    useCases: [
      { title: 'Off-season listings', body: 'Show listings in autumn, winter or drought conditions with the grass at its summer best.' },
      { title: 'Cover & hero photos', body: 'Lift the lead photo of any property by giving the lawn the look of a freshly maintained garden.' },
      { title: 'New developments', body: 'Show landscaped finishes before grass has fully grown in around new builds.' },
      { title: 'Aerial / drone shots', body: 'Replace patchy ground cover in top-down photos for clean, magazine-grade overheads.' },
    ],
    stats: [
      { value: 'Any season', label: 'Summer-fresh lawns year-round' },
      { value: '1 click', label: 'No re-sodding or re-shooting' },
      { value: 'Photo-real', label: 'Matches light, shadow and perspective' },
    ],
    faqs: [
      { q: 'Will the new lawn look fake?', a: 'No. The AI matches lighting, perspective and shadows from the original photo so the new grass blends naturally into the scene.' },
      { q: 'Is this allowed on real-estate portals?', a: 'Yes, on every major portal — provided enhanced photos are disclosed where your local regulator requires it. The property itself is unchanged.' },
      { q: 'Can it replace lawns in back gardens too?', a: 'Yes — it works on front lawns, back gardens, side yards and aerial / drone shots of land.' },
    ],
    ctaHeadline: 'Give every listing perfect curb appeal',
    ctaSubtext: 'Turn a patchy, brown lawn into a lush green one in seconds — no landscaping, no re-shoot.',
  },

  {
    id: 'custom-prompt',
    icon: PenTool,
    title: 'Custom AI Prompt',
    tagline: 'Tell the AI exactly what you want changed',
    shortPitch: 'Describe any edit — the AI handles the rest.',
    description:
      'Have something specific in mind? Use our custom prompt feature to describe exactly what changes you want. Remove specific objects, change the sky, add landscaping, or make any other creative edits.',
    benefits: [
      'Remove unwanted objects or people',
      'Replace overcast skies with blue skies',
      'Add or remove landscaping elements',
      'Complete creative control',
    ],
    beforeImage: customPromptBefore,
    afterImage: customPromptAfter,
    whyItHelps: [
      'Fix the small things that ruin good photos — bins, cars, cables, grey skies — without re-shooting.',
      'No Photoshop skills needed: type what you want in plain English.',
      'Works on edge cases the standard enhancements don\'t cover.',
    ],
    sellingImpact: [
      'Removes distractions that pull buyer attention away from the property itself.',
      'A single clean, focused photo can outperform ten cluttered ones in saved-listing rates.',
      'Helps the property look its absolute best — which directly supports a stronger asking price.',
    ],
    seoTitle: 'Custom AI Photo Editing for Real Estate | ListBright',
    seoDescription: 'Type any edit you want and the AI handles it. Remove cars, bins, people, replace skies, add landscaping — no Photoshop required.',
    keywords: ['AI photo editor real estate', 'remove objects from photo AI', 'sky replacement', 'declutter listing photos', 'custom prompt photo editor'],
    longDescription: [
      'Sometimes the perfect shot has one wrong thing in it — a parked car, a wheelie bin, a power line, a passer-by, a grey sky. ListBright\'s Custom AI Prompt lets you fix it in plain English: type what you want changed, and the AI rewrites that part of the photo.',
      'It works for object removal, sky replacement, landscaping additions, surface cleaning, lighting adjustments and dozens of other small tweaks that traditionally needed a Photoshop expert and an hour of work.',
      'Use it as the final polish step after our standard enhancements — clean photos, focused on the property, with nothing pulling buyer attention away.',
    ],
    useCases: [
      { title: 'Object removal', body: 'Bins, cars, cables, signage, satellite dishes, garden hoses — describe it, and it\'s gone.' },
      { title: 'Sky replacement', body: 'Swap a grey, overcast sky for a clear blue one or a dramatic golden-hour gradient.' },
      { title: 'Add landscaping', body: 'Add greenery, flowers or trim a patchy lawn to lift curb appeal.' },
      { title: 'Privacy edits', body: 'Blur or remove people, faces and licence plates to comply with privacy rules.' },
    ],
    stats: [
      { value: 'Plain English', label: 'No Photoshop skills needed' },
      { value: 'Per photo', label: 'Apply to one shot or many at once' },
      { value: 'Seconds', label: 'Not hours of retouching' },
    ],
    faqs: [
      { q: 'How specific can I be?', a: 'Very. You can target one element ("remove the red car on the left"), change the environment ("make the lawn greener"), or restyle entire areas ("replace the sky with sunset and add warm light on the house").' },
      { q: 'Will the rest of the photo stay the same?', a: 'Yes. The model only changes what you describe and preserves the rest of the scene, including original perspective and proportions.' },
      { q: 'Is this better than standard enhancement?', a: 'It\'s a complement, not a replacement. Run standard enhancements first, then use Custom Prompt for the small fixes that make a great photo perfect.' },
    ],
    ctaHeadline: 'Fix the one thing ruining your best photo',
    ctaSubtext: 'Describe the change in plain English — the AI does the rest. Try it on your trickiest listing photo today.',
  },

  {
    id: 'cinematic-video',
    icon: Clapperboard,
    title: 'Cinematic AI Video',
    tagline: 'True AI-generated video clips — not just a slideshow',
    shortPitch: 'Real AI motion video from your photos for $5.',
    description:
      'Send each of your property photos to a state-of-the-art AI video model and get back genuine cinematic motion clips — dolly-ins, pans, drift shots — stitched into one polished video. Not a slideshow. Actual AI-generated movement on every scene.',
    benefits: [
      'Real AI motion on every photo — dolly, pan, orbit, float',
      'Up to 8 cinematic clips stitched into one video',
      'Sorted by room type for a natural tour flow',
      'Download as HD video, ready to share anywhere',
    ],
    hasComparison: false,
    sampleVideo: '/sample-cinematic.mp4',
    sampleVideoSourcePhotos: [
      { src: heroAfter, label: 'Exterior — Hero' },
      { src: goldenHourAfter, label: 'Golden Hour' },
      { src: stagingAfter, label: 'Living Room' },
      { src: qualityAfter, label: 'Interior Detail' },
      { src: clutterAfter, label: 'Clean Interior' },
      { src: skyLawnAfter, label: 'Garden View' },
    ],
    whyItHelps: [
      'AI-generated motion makes your listing feel alive — static photos and basic slideshows can\'t compete.',
      'Each shot uses a different cinematic move, so the video feels professionally directed.',
      'No camera crew, no drone, no editing software — fully automated for $5.',
    ],
    sellingImpact: [
      'Video listings generate significantly more enquiries than photo-only listings on every major portal.',
      'Cinematic footage builds an emotional connection before buyers ever set foot in the property.',
      'Standing out in a sea of flat photos is the fastest way to more clicks, more viewings, and higher offers.',
    ],
    seoTitle: 'Cinematic AI Property Video — Real Motion Clips | ListBright',
    seoDescription: 'Send your photos to a state-of-the-art AI video model and get genuine cinematic motion clips — dolly, pan, orbit — stitched into one polished property video.',
    keywords: ['AI property video', 'cinematic real estate video', 'photo to AI video', 'AI video tour real estate', 'image to video AI'],
    longDescription: [
      'This is not a slideshow with motion effects — it\'s real AI-generated video. Each of your property photos is sent to a state-of-the-art image-to-video model that synthesises genuine camera movement: dolly-ins on facades, pans across living rooms, slow orbits around features and floating drift shots through hallways.',
      'The clips are then sorted by room type for a natural tour flow, stitched together, scored with cinematic music and exported as one HD video — ready for portals, Reels, TikTok, YouTube and high-end client presentations.',
      'For a flat fee, you get the kind of cinematic listing video that previously required a videographer, a gimbal operator and a full editing day. ListBright produces it from your existing photos, fully automatically.',
    ],
    useCases: [
      { title: 'Luxury & high-ticket listings', body: 'Match the production value buyers expect at the premium end of the market.' },
      { title: 'Drone-style flyovers', body: 'Get sweeping motion on exteriors without owning, flying or insuring a drone.' },
      { title: 'Agency social content', body: 'Feed your Reels/TikTok pipeline with cinematic property footage every week.' },
      { title: 'Investor decks', body: 'Embed a cinematic clip in pitch decks and offering memos for serious buyers.' },
    ],
    stats: [
      { value: 'Up to 8', label: 'AI motion clips per video' },
      { value: 'HD export', label: 'Portal & social ready' },
      { value: 'No crew', label: 'No videographer or drone needed' },
    ],
    faqs: [
      { q: 'How is this different from a slideshow video?', a: 'A slideshow animates static photos with pan/zoom effects. The Cinematic AI Video generates entirely new motion frames per photo using an AI video model — every shot has real camera movement.' },
      { q: 'Can I pick which photos become motion clips?', a: 'Yes. Choose up to 8 of your best photos and the AI generates one cinematic clip per photo, then stitches them into a single video.' },
      { q: 'What can I do with the final video?', a: 'Download as HD MP4 and post anywhere — portals, your microsite, Instagram Reels, TikTok, YouTube Shorts, WhatsApp, email campaigns or client presentations.' },
    ],
    ctaHeadline: 'Give your listing real cinematic motion',
    ctaSubtext: 'Pick your best photos — get a polished, AI-generated cinematic property video in minutes.',
  },

  {
    id: 'ai-descriptions',
    icon: FileText,
    title: 'AI-Powered Descriptions',
    tagline: 'Compelling copy that sells properties faster',
    shortPitch: 'Persuasive, SEO-friendly copy in 28 languages.',
    description:
      'Our AI analyzes your photos and property details to generate persuasive, SEO-optimized descriptions. Choose your tone, length, and style — get ready-to-publish copy in seconds.',
    benefits: [
      'SEO-optimized for better search visibility',
      'Multiple tone options (professional, friendly, luxury)',
      'Customizable length and style',
      'Available in 28 languages',
    ],
    hasComparison: false,
    whyItHelps: [
      'Most listing copy is generic and forgettable — strong copy stops the scroll.',
      'Saves hours of writer\'s block; you get a polished draft in seconds.',
      'Translations open the property to non-local buyers without paying a translator.',
    ],
    sellingImpact: [
      'Better-written descriptions are proven to increase enquiry rates on real-estate portals.',
      'SEO-friendly text helps the listing rank in Google searches buyers actually type.',
      'Multilingual versions expand the buyer pool — and a bigger pool supports a higher final price.',
    ],
    seoTitle: 'AI Real Estate Listing Description Generator | ListBright',
    seoDescription: 'Generate persuasive, SEO-optimised listing descriptions in 28 languages. Tone, length and style — tailored to your property in seconds.',
    keywords: ['AI listing description generator', 'real estate copywriting AI', 'property description writer', 'multilingual listing descriptions', 'SEO real estate copy'],
    longDescription: [
      'Most listing copy reads the same: "spacious living room, modern kitchen, well-maintained throughout." That sameness is why buyers skim past 90% of descriptions. ListBright analyses your photos and property facts to generate persuasive, specific copy that actually sounds like the home.',
      'Pick a tone — professional, friendly, luxury, lifestyle — and a length, and the AI writes a polished, SEO-optimised description ready to paste into any portal. It naturally includes the long-tail keywords buyers search for, so your listing has a better chance of ranking on Google as well as on Zillow, Rightmove or Idealista.',
      'Need to reach international buyers? Generate the same description in any of 28 languages, with the local idioms and currency formatting built in.',
    ],
    useCases: [
      { title: 'Portal listing copy', body: 'Paste straight into Zillow, Rightmove, Idealista, Bayut, Booking.com and others.' },
      { title: 'International buyers', body: 'One-click translation into 28 languages widens your buyer pool overnight.' },
      { title: 'Social captions', body: 'Generate a short version for Instagram, Facebook and TikTok captions.' },
      { title: 'Email & WhatsApp blurbs', body: 'Send concise, persuasive intros to your buyer database in seconds.' },
    ],
    stats: [
      { value: '28 languages', label: 'Native-quality translations' },
      { value: '4 tones', label: 'Professional, friendly, luxury, lifestyle' },
      { value: 'SEO-tuned', label: 'Built for portal & Google search' },
    ],
    faqs: [
      { q: 'Will the copy sound like AI?', a: 'No. Descriptions are generated from your real photos and property data, with tone and pacing tuned for real-estate readers — not generic chatbot output.' },
      { q: 'Can I edit it before publishing?', a: 'Yes. Every description is fully editable, so you can tweak phrasing, add personal touches or insert your agency boilerplate.' },
      { q: 'Which languages are supported?', a: '28, including English, Spanish, French, German, Italian, Dutch, Portuguese, Greek, Arabic, Russian, Chinese, Japanese and more.' },
    ],
    ctaHeadline: 'Get a polished description in seconds',
    ctaSubtext: 'Persuasive, SEO-friendly copy in your tone and language — generated from your photos and property facts.',
  },

  {
    id: 'microsite',
    icon: Globe,
    title: 'Shareable Microsite',
    tagline: 'Every listing gets its own beautiful web page',
    shortPitch: 'A dedicated, mobile-ready web page per listing.',
    description:
      'Each listing automatically gets a dedicated, mobile-optimized web page with all your enhanced photos, descriptions, and contact options. Share the link anywhere or print the QR code.',
    benefits: [
      'Unique shareable URL for each listing',
      'Auto-generated QR code for print materials',
      'Mobile-optimized responsive design',
      'Multi-language support built-in',
    ],
    hasComparison: false,
    extraIcons: [Share2, QrCode, Smartphone, Languages],
    whyItHelps: [
      'A clean, single-property page beats a cluttered portal listing surrounded by competitors.',
      'You control the experience: your photos, your description, your contact details.',
      'Built-in multi-language support opens the property to international buyers automatically.',
    ],
    sellingImpact: [
      'Removes competition from the page — buyers focus only on your property.',
      'Easy to share on WhatsApp, email and social, where the strongest buyer leads come from.',
      'A premium-feeling page reinforces a premium asking price.',
    ],
    seoTitle: 'Single-Property Listing Microsite Builder | ListBright',
    seoDescription: 'Every listing gets its own beautiful, mobile-optimised website with photos, video, description and contact form. Share the link or print the QR code.',
    keywords: ['single property website', 'real estate microsite', 'property landing page', 'listing website builder', 'shareable real estate page'],
    longDescription: [
      'On a portal, your listing competes with dozens of others on the same page. On a ListBright microsite, the buyer sees only one thing: your property. That focus alone meaningfully improves enquiry rates.',
      'Each microsite is generated automatically from your listing — enhanced photos, AI-written description, video tour, key facts, map, gallery and a contact form — and ships on a fast, mobile-optimised, multi-language web page with its own URL and QR code.',
      'Share the link on WhatsApp, email, Instagram bio, business cards and flyers — wherever buyers actually message you from. Your microsite is always up to date with the latest version of the listing.',
    ],
    useCases: [
      { title: 'WhatsApp & DM sharing', body: 'Send one link instead of a folder of photos — buyers preview the home in seconds.' },
      { title: 'Instagram bio & link-in-bio', body: 'Drive social traffic to a focused, conversion-ready property page.' },
      { title: 'Email signatures', body: 'Add the QR or link to every outgoing email until the property is sold.' },
      { title: 'International buyers', body: 'Built-in translations open the listing to non-local audiences automatically.' },
    ],
    stats: [
      { value: 'Unique URL', label: 'And QR code per listing' },
      { value: 'Mobile-first', label: 'Fast on every device' },
      { value: '16 languages', label: 'Auto-translated content' },
    ],
    faqs: [
      { q: 'Do I need to build the website myself?', a: 'No. Each microsite is generated automatically from the listing you create — photos, copy, video and contact info are added for you.' },
      { q: 'Can I customise the URL?', a: 'Yes — listings get a clean, shareable URL plus a QR code you can print on flyers and window displays.' },
      { q: 'Will it work on phones?', a: 'Yes. Every microsite is fully mobile-optimised and loads in under a second on modern phones.' },
    ],
    ctaHeadline: 'Give every listing its own beautiful website',
    ctaSubtext: 'Auto-generated, mobile-first and multilingual — share the link anywhere buyers spend time.',
  },

  {
    id: 'slideshow-video',
    icon: Video,
    title: 'Property Slideshow Video',
    tagline: 'Create engaging video tours from your photos',
    shortPitch: 'Auto-generated video tour with music and transitions.',
    description:
      'Turn your enhanced property photos into a professional slideshow video with smooth transitions, background music, and property details. Perfect for social media and YouTube.',
    benefits: [
      'Automatic Ken Burns effect for dynamic movement',
      'Background music included',
      'Property details overlay',
      'Ready to share on social media',
    ],
    hasComparison: false,
    whyItHelps: [
      'Video is the most engaging format on Instagram, TikTok, Facebook and YouTube.',
      'No editing skills, software, or hours of work — generated automatically from your photos.',
      'Gives the property a “cinematic” feel that static images alone can\'t match.',
    ],
    sellingImpact: [
      'Listings with video are reported to receive substantially more enquiries than photo-only listings.',
      'Reels and Stories with property videos reach buyers who would never find your portal listing.',
      'A polished video tour builds trust and pre-qualifies buyers before they even book a viewing.',
    ],
    seoTitle: 'Real Estate Slideshow Video Maker | ListBright',
    seoDescription: 'Auto-create a polished property tour video from your photos — Ken Burns motion, music, captions and details. Ready for Reels, TikTok and YouTube.',
    keywords: ['real estate slideshow video', 'property video maker', 'listing video for Instagram', 'real estate Reels', 'photo to video AI'],
    longDescription: [
      'Video is the most-engaging format on Instagram, TikTok, Facebook and YouTube — and listings with video receive significantly more enquiries than photo-only listings on every major real-estate portal.',
      'ListBright stitches your enhanced property photos into a polished slideshow video with smooth Ken Burns motion, professional transitions, background music and a clean property-details overlay. No editing apps, no timelines, no learning curve.',
      'Export once and post everywhere — your portal listing, Instagram Reels, TikTok, YouTube Shorts, WhatsApp Status, and your microsite — to reach buyers wherever they scroll.',
    ],
    useCases: [
      { title: 'Instagram Reels & TikTok', body: 'Vertical-friendly tours that feed the algorithm and reach buyers outside your portal audience.' },
      { title: 'YouTube property tours', body: 'A lightweight alternative to a filmed walk-through, ready in minutes.' },
      { title: 'WhatsApp & email blasts', body: 'Share a single, easy-to-watch video instead of a heavy photo gallery.' },
      { title: 'Open-house screens', body: 'Loop the video on a tablet or TV during in-person viewings.' },
    ],
    stats: [
      { value: 'Auto-generated', label: 'No editing software needed' },
      { value: '15–60s', label: 'Optimised for social attention spans' },
      { value: 'Music + captions', label: 'Built in, ready to post' },
    ],
    faqs: [
      { q: 'Do I need any video editing experience?', a: 'No. The slideshow video is generated automatically from your photos, music and property details. You just download and share.' },
      { q: 'Can I use the music commercially?', a: 'Yes. ListBright includes background tracks that are cleared for use on portals and social platforms.' },
      { q: 'Is this the same as the Cinematic AI Video?', a: 'No. The slideshow uses your real photos with smooth motion and transitions. The Cinematic AI Video sends each photo through an AI video model that adds genuine camera movement to every shot.' },
    ],
    ctaHeadline: 'Turn your photos into a scroll-stopping video',
    ctaSubtext: 'Generate a ready-to-post property tour video — captions, music and motion included.',
  },

  {
    id: 'premium-description',
    icon: Crown,
    title: 'SEO Optimized Description',
    tagline: 'Research-backed, conversion-optimized prose',
    shortPitch: 'Research-backed copy proven to lift price and speed.',
    description:
      "Enhances your real estate listings with AI-powered photo analysis and research-optimized descriptions that help properties sell faster and for more money. Built on Zillow's analysis of 2 million home sales and university research into listing language, ListBright automatically uses the keywords proven to boost sale prices by up to 8%, generate up to 45% more clicks, and sell properties up to 20% faster — while blocking the words that cost sellers thousands.",
    benefits: [
      'Keywords proven to boost sale prices by up to 8%',
      'Generate up to 45% more clicks on your listings',
      'Sell properties up to 20% faster',
      'Price-tier calibrated vocabulary (luxury, mid-range, entry)',
    ],
    hasComparison: false,
    whyItHelps: [
      'Built on data from millions of real home sales — not just what “sounds nice”.',
      'Avoids accidental buzzwords (like “unique” or “cosy”) that research links to lower sale prices.',
      'Calibrates the vocabulary to the property\'s tier so luxury homes feel luxury and starter homes feel attainable.',
    ],
    sellingImpact: [
      'Up to 8% higher sale prices using keywords statistically tied to better outcomes.',
      'Up to 45% more clicks, which compounds into more viewings and more offers.',
      'Up to 20% faster sales — every week saved is mortgage interest and stress saved for the seller.',
    ],
    seoTitle: 'SEO Optimized Description — Research-Backed Listing Copy | ListBright',
    seoDescription: 'Listing descriptions built on Zillow\'s analysis of 2M home sales. Keywords proven to lift price up to 8%, clicks 45% and time-to-sale 20%.',
    keywords: ['research-backed listing copy', 'Zillow keywords description', 'words that sell homes', 'real estate description optimisation', 'premium property copywriting'],
    longDescription: [
      'SEO Optimized Description goes beyond a well-written paragraph. It\'s built on Zillow\'s landmark study of 2 million home sales and on academic research into the language buyers respond to — then encoded into ListBright\'s generator.',
      'The model preferentially uses words statistically associated with higher sale prices and faster sales (e.g. "captivating", "luxurious", "landscaped", "stainless"), and avoids words quietly tied to lower outcomes (e.g. "unique", "cosy", "investor"). Vocabulary is also calibrated to the property\'s price tier, so luxury homes feel luxury and entry-level homes feel attainable.',
      'The result is copy that doesn\'t just read well — it measurably nudges every metric that determines how quickly and for how much your listing sells.',
    ],
    useCases: [
      { title: 'Top-of-market homes', body: 'Match the language buyers expect at the high end without sounding generic.' },
      { title: 'Hard-to-shift listings', body: 'Re-write tired copy with research-backed keywords to reset the listing.' },
      { title: 'New developments', body: 'Apply consistent, conversion-optimised copy across an entire unit mix.' },
      { title: 'Investor properties', body: 'Reframe rental and yield language without using words shown to lower offers.' },
    ],
    stats: [
      { value: '+8%', label: 'Sale price uplift (Zillow data)' },
      { value: '+45%', label: 'More clicks on listings' },
      { value: '−20%', label: 'Faster time to sale' },
    ],
    faqs: [
      { q: 'Where do these numbers come from?', a: 'They\'re drawn from Zillow\'s analysis of approximately 2 million home sales and supporting academic research into listing language and buyer response.' },
      { q: 'Will it work outside the US?', a: 'Yes. The underlying linguistic patterns (positive sensory language, specific finishes, lifestyle framing) translate well across every English-speaking market and every language we support.' },
      { q: 'Is this included in the standard description?', a: 'SEO Optimized Description is generated in parallel for Growth and Team plans, so you get both the standard description and the research-backed version on every listing.' },
    ],
    ctaHeadline: 'Use the words proven to sell homes',
    ctaSubtext: 'Generate a research-backed, conversion-optimised description on your next listing — included on Growth and Team.',
  },

  {
    id: 'pdf-flyer',
    icon: FileImage,
    title: 'PDF Property Flyer',
    tagline: 'Professional print-ready marketing materials',
    shortPitch: 'Print-ready flyers with QR code in one click.',
    description:
      'Generate beautiful PDF flyers featuring your best photos, property details, and contact information. Print them for open houses, mail to prospects, or share digitally.',
    benefits: [
      'Professional design templates',
      'QR code linking to microsite',
      'Customizable with your branding',
      'Print-ready high resolution',
    ],
    hasComparison: false,
    whyItHelps: [
      'Open houses, mailbox drops, and window displays still convert serious local buyers.',
      'No designer needed — the layout, photos, and details are assembled for you.',
      'The built-in QR code bridges print and digital, sending people straight to the microsite.',
    ],
    sellingImpact: [
      'Professional-looking print materials position the property as premium from the first glance.',
      'Mailbox flyers reach neighbours, who are statistically a major source of buyer referrals.',
      'Stronger marketing visibility means more demand — and more demand supports a higher price.',
    ],
    seoTitle: 'Real Estate PDF Flyer Generator with QR Code | ListBright',
    seoDescription: 'Generate print-ready property flyers with photos, details and a QR code linking to your listing microsite. Perfect for open houses and mailbox drops.',
    keywords: ['real estate flyer template', 'property flyer generator', 'open house flyer', 'real estate PDF brochure', 'QR code listing flyer'],
    longDescription: [
      'Even in a digital-first market, print materials still convert serious local buyers — at open houses, in window displays, at agency receptions and through neighbourhood mailbox drops. ListBright generates professional, print-ready PDF flyers from your listing in one click.',
      'Each flyer features your enhanced photos, the AI-written description, key property facts, your contact details and a QR code that links straight to the listing\'s microsite. No graphic designer, no template fiddling, no exporting from Canva.',
      'Localised in 14 languages and tuned for high-resolution print, ListBright flyers look as good in your hand as they do on screen.',
    ],
    useCases: [
      { title: 'Open-house handouts', body: 'Walk every visitor away with a tangible, branded reminder of the property.' },
      { title: 'Neighbourhood mailbox drops', body: 'Reach the buyer pool agents under-use: neighbours and their referral network.' },
      { title: 'Agency window displays', body: 'Refresh window cards instantly when a listing changes.' },
      { title: 'Investor & developer packs', body: 'Drop a flyer into pitch decks or unit-sale packets in seconds.' },
    ],
    stats: [
      { value: '14 languages', label: 'Localised flyer copy' },
      { value: 'Print-ready', label: 'High-res PDF, A4 & US Letter' },
      { value: 'QR included', label: 'Bridges print to your microsite' },
    ],
    faqs: [
      { q: 'Is the PDF print-ready?', a: 'Yes. Flyers export at high resolution suitable for professional and home printing on A4 or US Letter paper.' },
      { q: 'Where does the QR code lead?', a: 'Straight to the listing\'s shareable microsite, so anyone who scans gets the full photo gallery, video and contact options on their phone.' },
      { q: 'Can I change the language?', a: 'Yes — flyers can be generated in any of 14 supported languages, with localised copy and currency formatting.' },
    ],
    ctaHeadline: 'Print-ready flyers in one click',
    ctaSubtext: 'Photos, copy, contact details and a QR code — a complete property flyer, ready to print and hand out.',
  },
];

export function getFeatureBySlug(slug: string): FeatureItem | undefined {
  return features.find((f) => f.id === slug);
}